import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { MessageDescriptor } from 'react-intl';
import { useMessagingStore, useChatRehydration } from '../hooks/index.ts';
import { useMessagingService } from '../hooks/useMessagingService.ts';
import { useLocalization } from '../localization/LocalizationProvider.tsx';
import { StageHeader, type AgentLogo } from './StageHeader.tsx';
import { StageLayout } from './StageLayout.tsx';
import { StageVoice } from './StageVoice.tsx';
import { useStageController } from './useStageController.ts';
import { useStageAppearance } from './useStageAppearance.ts';
import { useA2uiDemoHook } from './demo-fixture.ts';

export interface StageShellProps {
  logo?: AgentLogo;
  appearance?: 'light' | 'dark' | 'auto';
  placeholder?: MessageDescriptor;
}

/** Standalone entry with the normal agent arrival and parent preview integration. */
export const StageShell = observer(function StageShell({
  logo,
  appearance = 'light',
  placeholder,
}: StageShellProps) {
  useMessagingService();
  useStageAppearance(appearance);
  useA2uiDemoHook();
  const store = useMessagingStore();
  const localization = useLocalization();
  const [initError, setInitError] = useState(false);
  const onError = useCallback(() => setInitError(true), []);
  const connection = useChatRehydration({ onError });
  const stage = useStageController({
    connection: { ...connection, initError },
    canSubmit: connection.isReady && connection.connectionStatus === 'connected',
    submitMessage: store.sendMessage,
  });
  return (
    <div className="stage-frame">
      <StageLayout
        stage={stage}
        placeholder={placeholder}
        header={
          <StageHeader
            logo={logo}
            currentLocale={localization.messageLocale}
            onRestart={stage.sendMessage}
            onLanguageSelect={(instruction) => stage.sendMessage(instruction)}
            disabled={stage.runInFlight || !stage.ready}
          />
        }
      >
        <StageVoice screenSelection={stage.voiceScreenSelection} onRecord={stage.sendAudio} />
      </StageLayout>
    </div>
  );
});
